/**
 * "Active bonuses" hero — pinned at the top of /promos when the player has
 * 1+ in-progress claim. Horizontal swipe carousel when multiple. Dashed
 * placeholder card rendered when zero.
 */
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Sparkles } from 'lucide-react';
import { Money } from '@/components/money/Money.js';
import { useFormatMoney } from '@/components/money/MoneyProvider.js';
import {
  bigIntish,
  turnoverProgress,
  type PromotionClaim,
} from '@/features/promo/api.js';
import { TermTooltip } from './TermTooltip.js';
import { formatDurationShort } from '@/lib/datetime/duration.js';

interface Props {
  claims: PromotionClaim[];
  currency: string;
}

function relativeExpiry(iso: string | null | undefined, t: (k: string) => string): string | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(ms) || ms <= 0) return null;
  return formatDurationShort(Math.floor(ms / 1000), t);
}

export function ActiveBonusHero({ claims, currency }: Props) {
  const { t } = useTranslation('promo');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();

  // A pending first_deposit/reload reservation has turnoverRequired = 0 (the
  // bonus hasn't credited yet) — showing it here as a ฿0/฿0 progress card is
  // misleading, so only surface claims with a real bonus in progress. Its card
  // below still shows the "Deposit to activate" CTA.
  const live = claims.filter((c) => bigIntish(c.turnoverRequired) > 0n);

  if (live.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-[var(--border)] bg-[var(--bg-raised)] px-4 py-2.5 text-center flex items-center justify-center gap-2">
        <Sparkles size={14} className="text-[#fbbf24]" />
        <span className="text-[11.5px] text-[var(--text-muted)]">{t('hero.empty')}</span>
      </div>
    );
  }

  // Match the 2-col promo grid below: 1 active bonus = 1 card in the left
  // column, the right column stays empty. 2+ active bonuses fill the row,
  // additional claims wrap. Keeps card width consistent with promo cards.
  return (
    <div className="grid gap-3 md:grid-cols-2 auto-rows-fr">
      {live.map((c) => {
        const need = bigIntish(c.turnoverRequired);
        const done = bigIntish(c.turnoverAchieved);
        const remaining = done >= need ? 0n : need - done;
        const pct = Math.min(100, turnoverProgress(c) * 100);
        const expIn = relativeExpiry(c.expiresAt, tc);
        const cur = c.currency ?? currency;

        return (
          <article
            key={c.id}
            className="h-full rounded-2xl text-[#1f2937] p-4 flex flex-col"
            style={{ background: 'linear-gradient(135deg,#fbbf24,#f59e0b)' }}
          >
            <div className="text-[10px] font-bold uppercase tracking-wide opacity-70 flex items-center gap-1">
              {t('hero.title')} <TermTooltip term="unlock" claim={c} currency={cur} />
            </div>
            <div className="flex items-center justify-between mt-1.5 mb-2.5">
              <span className="text-[13px] font-bold truncate">
                {c.promotionTitle ?? t('hero.title')}
              </span>
              {expIn && (
                <span className="text-[11px] bg-black/15 px-2 py-0.5 rounded-xl">
                  {t('hero.expiresIn', { time: expIn })}
                </span>
              )}
            </div>
            <div className="bg-black/20 h-2 rounded-md overflow-hidden">
              <div
                className="h-full bg-[#1f2937] rounded-md"
                style={{ width: `${pct}%` }}
              />
            </div>
            <div className="flex justify-between text-[11px] mt-1.5">
              <span><Money minor={done} currency={cur} /></span>
              <span><Money minor={need} currency={cur} /></span>
            </div>
            <HeroCta
              remaining={remaining}
              currency={cur}
              onClick={() => navigate({ to: remaining === 0n ? '/wallet/withdraw' : '/casino' })}
            />
          </article>
        );
      })}
    </div>
  );
}

function HeroCta({
  remaining,
  currency,
  onClick,
}: {
  remaining: bigint;
  currency: string;
  onClick: () => void;
}) {
  const { t } = useTranslation('promo');
  const formatMoney = useFormatMoney();
  if (remaining === 0n) {
    return (
      <button
        onClick={onClick}
        className="mt-2.5 w-full bg-[#1f2937] text-white py-2 rounded-lg text-[12px] font-bold"
      >
        {t('hero.goPlay')}
      </button>
    );
  }
  // hero.wagerMore reads "Wager {{amount}} more · Find a game →" in EN /
  // "เดิมพันอีก {{amount}} · ไปหาเกม →" in TH. Format the bigint via the
  // shared money helper so the locale-aware ` ฿130.00` interpolates
  // inline — replaces the previous hardcoded English "Wager … more".
  return (
    <button
      onClick={onClick}
      className="mt-2.5 w-full bg-[#1f2937] text-white py-2 rounded-lg text-[12px] font-bold flex items-center justify-center gap-1"
    >
      {t('hero.wagerMore', { amount: formatMoney(remaining, currency) })}
    </button>
  );
}
